import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getAPIEndpoint, getWebappAPIEndpoint } from './endpoint';
import { useAuth0 } from '@auth0/auth0-react';

export // A custom hook that builds on useLocation to parse
// the query string for you.
function useQueryParam(key: string, initialValue: string): [string, (newValue: string) => void] {
  const { state, hash, pathname, search } = useLocation();
  let history = useHistory();

  let value: string = initialValue;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const alreadyDefinedValue = query.get(key);
  if (alreadyDefinedValue !== null && alreadyDefinedValue !== '') {
    value = alreadyDefinedValue;
  }

  const dispatch = (newValue: string) => {
    // do not update history if nothing has changed
    if (newValue === query.get(key)) {
      return;
    }
    if (newValue !== '') {
      query.set(key, newValue);
    } else {
      if (!query.has(key)) {
        // no need to update if newValue is '' and query already does not include it
        return;
      }
      query.delete(key);
    }
    history.push({
      state: state,
      hash: hash,
      pathname: pathname,
      search: '?' + query.toString(),
    });
  };

  return [value, dispatch];
}

export const fetchAuthorizedAPIEndpoint = (pathAndQuery: string, accessToken: string | undefined, init?: RequestInit, tsdbUrl?: string): Promise<Response> => {
  if (!accessToken) throw new Error('accessToken is undefined');

  if (!init) {
    init = {
      headers: [['Authorization', `Bearer ${accessToken}`]],
    };
  } else init.headers = [['Authorization', `Bearer ${accessToken}`]];

  const input = `${getAPIEndpoint(tsdbUrl)}${pathAndQuery}`;
  return fetch(input, init);
};

export const fetchAuthorizedWebappAPIEndpoint = (pathAndQuery: string, accessToken: string | undefined, init?: RequestInit): Promise<Response> => {
  if (!accessToken) throw new Error('accessToken is undefined');

  if (!init) {
    init = {
      headers: [['Authorization', `Bearer ${accessToken}`]],
    };
  } else init.headers = [['Authorization', `Bearer ${accessToken}`]];

  const input = `${getWebappAPIEndpoint()}${pathAndQuery}`;
  return fetch(input, init);
};

export const useAuth0OrQueryAuth = (): string | undefined => {
  const [finalAccessToken, setFinalAccessToken] = useState<string | undefined>();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [auth0AccessToken, setAuth0AccessToken] = useState<string | undefined>();

  const { search } = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAuth0AccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) setFinalAccessToken(auth0AccessToken);
    else {
      let accessToken = urlParams.get('accessToken');
      if (accessToken !== null) {
        setFinalAccessToken(accessToken);
      }
    }
  }, [auth0AccessToken, isAuthenticated, search, urlParams]);

  return finalAccessToken;
};

export const hasAuth0Prefix = (sub: string): boolean => {
  const defaultPrefix = 'auth0|';
  if (sub.includes(defaultPrefix)) return true;
  else return false;
};

export const trimAuth0Prefix = (sub: string): string => {
  return sub.split('|')[1] || '';
};

export const userIsAuthorSession = (sub: string, ID: string): boolean => {
  if (hasAuth0Prefix(sub)) return trimAuth0Prefix(sub) === ID;
  else return sub === ID;
};

export const getUserSub = (sub: string): string => {
  if (hasAuth0Prefix(sub)) return trimAuth0Prefix(sub);
  else return sub;
};
